// src/components/PDF/WorkOrderPDF.jsx
import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';

// Style dengan logo & signature
const styles = StyleSheet.create({
  page: { 
    padding: 40, 
    fontSize: 10,
    fontFamily: 'Helvetica'
  },

  // HEADER
  header: { 
    flexDirection: 'row', 
    justifyContent: 'space-between', 
    alignItems: 'center',
    marginBottom: 20,
    borderBottom: '2px solid #2563eb',
    paddingBottom: 12
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12
  },
  logo: {
    width: 50,
    height: 50
  },
  title: { 
    fontSize: 22, 
    fontWeight: 'bold',
    color: '#2563eb',
    letterSpacing: 1
  },
  subtitle: {
    fontSize: 8,
    color: '#666'
  },
  woNumber: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#1a1a2e'
  },
  statusBadge: {
    fontSize: 8,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
    marginTop: 2,
    textAlign: 'center'
  },
  
  // SECTION
  section: { 
    marginBottom: 12 
  },
  sectionTitle: {
    fontWeight: 'bold',
    fontSize: 11,
    marginBottom: 4,
    color: '#374151'
  },

  // ROW
  row: { 
    flexDirection: 'row', 
    justifyContent: 'space-between', 
    paddingVertical: 2 
  },
  label: { 
    fontSize: 9, 
    color: '#666',
    width: '35%'
  },
  value: { 
    fontSize: 9, 
    fontWeight: 'bold',
    width: '65%',
    textAlign: 'right'
  },

  // TABLE
  tableHeader: { 
    flexDirection: 'row', 
    borderBottom: '1px solid #ccc', 
    paddingVertical: 4,
    backgroundColor: '#f3f4f6'
  },
  tableRow: { 
    flexDirection: 'row', 
    borderBottom: '1px solid #eee', 
    paddingVertical: 3 
  },
  col1: { width: '50%', fontSize: 9 },
  col2: { width: '20%', fontSize: 9, textAlign: 'center' },
  col3: { width: '30%', fontSize: 9, textAlign: 'right' },

  // TOTAL
  total: { 
    marginTop: 12, 
    borderTop: '2px solid #2563eb', 
    paddingTop: 10 
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 2
  },
  grandTotal: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderTop: '2px solid #1a1a2e',
    paddingTop: 6,
    marginTop: 4
  },

  // TEAM
  teamContainer: { 
    flexDirection: 'row', 
    flexWrap: 'wrap', 
    gap: 4, 
    marginTop: 4 
  },
  teamBadge: { 
    paddingHorizontal: 6, 
    paddingVertical: 2, 
    borderRadius: 4, 
    fontSize: 8, 
    backgroundColor: '#e0e7ff',
    color: '#3730a3'
  },
  teamLead: { 
    backgroundColor: '#fef3c7',
    color: '#92400e'
  },

  // SIGNATURE
  signatureSection: {
    marginTop: 24,
    paddingTop: 12,
    borderTop: '1px solid #e5e7eb',
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  signatureBox: {
    width: '45%',
    alignItems: 'center'
  },
  signatureLine: {
    borderTop: '1px solid #000',
    width: 120,
    marginTop: 16,
    marginBottom: 4
  },
  signatureName: {
    fontSize: 9,
    fontWeight: 'bold'
  },
  signatureRole: {
    fontSize: 8,
    color: '#666'
  },

  // FOOTER
  footer: {
    marginTop: 16,
    borderTop: '1px solid #eee',
    paddingTop: 8,
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  footerText: {
    fontSize: 7,
    color: '#999'
  }
});

const formatCurrency = (amount) => {
  if (!amount || isNaN(amount)) return 'Rp 0';
  return `Rp ${Math.round(amount).toLocaleString('id-ID')}`;
};

const formatDate = (date) => {
  if (!date) return '-';
  try {
    return new Date(date).toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  } catch (e) {
    return '-';
  }
};

const getStatusColor = (status) => {
  const colors = {
    'pending': '#f59e0b',
    'in-progress': '#3b82f6',
    'completed': '#10b981',
    'delivered': '#8b5cf6',
    'cancelled': '#ef4444'
  };
  return colors[status] || '#6b7280';
};

const WorkOrderPDF = ({ 
  wo = {}, 
  customer = {}, 
  staff = {},
  signature = null 
}) => {
  const team = wo?.team || [];
  const materials = wo?.materials || [];
  const laborCost = wo?.laborCost || 0;
  const totalCost = wo?.totalCost || 0;
  const statusColor = getStatusColor(wo?.status);

  // Ambil staff name dari WO (penanggung jawab)
  const signerName = staff?.name || wo?.staffName || 'Admin';
  const signerRole = staff?.level || wo?.staffRole || 'Manager';

  return (
    <Document>
      <Page size="A4" style={styles.page}>

        {/* ============================================================
            HEADER — DENGAN LOGO
            ============================================================ */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <Image src="public/trakhir.png" style={styles.logo} />
            <View>
              <Text style={styles.title}>WORK ORDER</Text>
              <Text style={styles.subtitle}>AKASIA WORKSHOP</Text>
            </View>
          </View>
          <View>
            <Text style={styles.woNumber}>{wo?.woNumber || '-'}</Text>
            <Text style={{ fontSize: 8, color: '#666', textAlign: 'right' }}>
              {formatDate(wo?.createdAt)}
            </Text>
            <Text style={[
              styles.statusBadge,
              { 
                backgroundColor: statusColor + '20', 
                color: statusColor 
              }
            ]}>
              {wo?.status || 'Pending'}
            </Text>
          </View>
        </View>

        {/* ============================================================
            CUSTOMER
            ============================================================ */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Customer</Text>
          <View style={styles.row}>
            <Text style={styles.label}>Nama</Text>
            <Text style={styles.value}>{customer?.name || '-'}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Telepon</Text>
            <Text style={styles.value}>{customer?.phone || '-'}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Alamat</Text>
            <Text style={styles.value}>{customer?.address || '-'}</Text>
          </View>
        </View>

        {/* ============================================================
            WORK ORDER INFO
            ============================================================ */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Informasi Pekerjaan</Text>
          <View style={styles.row}>
            <Text style={styles.label}>Proyek</Text>
            <Text style={styles.value}>{wo?.projectName || '-'}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Kode Proyek</Text>
            <Text style={styles.value}>{wo?.projectCode || '-'}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Kategori</Text>
            <Text style={styles.value}>{wo?.category || '-'}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Objek</Text>
            <Text style={styles.value}>{wo?.categoryItem || '-'}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Tanggal Mulai</Text>
            <Text style={styles.value}>{formatDate(wo?.startDate)}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Target Selesai</Text>
            <Text style={styles.value}>{formatDate(wo?.targetDate)}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Estimasi Hari</Text>
            <Text style={styles.value}>{wo?.estimatedDays || '-'} hari</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Progress</Text>
            <Text style={styles.value}>{wo?.progress || 0}%</Text>
          </View>
        </View>

        {/* ============================================================
            TEAM
            ============================================================ */}
        {team.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Tim Pengerjaan</Text>
            <View style={styles.teamContainer}>
              {team.map((member, idx) => (
                <Text key={idx} style={[styles.teamBadge, member.isLead && styles.teamLead]}>
                  {member.isLead ? '⭐ ' : ''}{member.name} ({member.role || 'Anggota'} • {member.hours || 0}j)
                </Text>
              ))}
            </View>
          </View>
        )}

        {/* ============================================================
            MATERIALS
            ============================================================ */}
        {materials.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Material</Text>
            <View style={styles.tableHeader}>
              <Text style={styles.col1}>Nama</Text>
              <Text style={styles.col2}>Qty</Text>
              <Text style={styles.col3}>Total</Text>
            </View>
            {materials.map((item, i) => (
              <View key={i} style={styles.tableRow}>
                <Text style={styles.col1}>{item?.name || 'Material'}</Text>
                <Text style={styles.col2}>{item?.qty || 0}</Text>
                <Text style={styles.col3}>{formatCurrency((item?.qty || 0) * (item?.price || 0))}</Text>
              </View>
            ))}
          </View>
        )}

        {/* ============================================================
            TOTAL
            ============================================================ */}
        <View style={styles.total}>
          <View style={styles.totalRow}>
            <Text style={styles.label}>Material</Text>
            <Text style={styles.value}>{formatCurrency(totalCost - laborCost)}</Text>
          </View>
          <View style={styles.totalRow}>
            <Text style={styles.label}>Labor</Text>
            <Text style={styles.value}>{formatCurrency(laborCost)}</Text>
          </View>
          <View style={styles.grandTotal}>
            <Text style={{ fontSize: 12, fontWeight: 'bold', color: '#2563eb' }}>TOTAL BIAYA</Text>
            <Text style={{ fontSize: 12, fontWeight: 'bold', color: '#2563eb' }}>{formatCurrency(totalCost)}</Text>
          </View>
        </View>

        {/* ============================================================
            SIGNATURE — TTD
            ============================================================ */}
        <View style={styles.signatureSection}>
          
          {/* Left: Penerima */}
          <View style={styles.signatureBox}>
            <Text style={{ fontSize: 8, color: '#666', marginBottom: 4 }}>Penerima</Text>
            <View style={{ height: 30 }} />
            <View style={styles.signatureLine} />
            <Text style={styles.signatureName}>-</Text>
            <Text style={styles.signatureRole}>Nama & Tanda Tangan</Text>
          </View>

          {/* Right: Penanggung Jawab / Pemberi */}
          <View style={styles.signatureBox}>
            <Text style={{ fontSize: 8, color: '#666', marginBottom: 4 }}>Penanggung Jawab</Text>
            
            {/* Signature Image (kalau ada) */}
            {signature ? (
              <Image src={signature} style={{ width: 80, height: 30 }} />
            ) : (
              <View style={{ height: 20 }} />
            )}
            
            <View style={styles.signatureLine} />
            <Text style={styles.signatureName}>{signerName}</Text>
            <Text style={styles.signatureRole}>{signerRole}</Text>
          </View>
        </View>

        {/* ============================================================
            FOOTER
            ============================================================ */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>
            Status Pembayaran: {wo?.paymentStatus || 'pending'}
          </Text>
          <Text style={styles.footerText}>
            AKASIA WORKSHOP • {new Date().getFullYear()}
          </Text>
        </View>

      </Page>
    </Document>
  );
};

export default WorkOrderPDF;